import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { 
  CheckCircle, 
  AlertTriangle,
  XCircle
} from 'lucide-react';

interface ReviewStepV2Props {
  data: any;
  onUpdate: (data: any) => void;
  programType?: string;
  formData?: any;
  isEditMode?: boolean;
  onNavigateToStep?: (stepIndex: number) => void;
  onSubmit?: () => void;
  stepProgress?: number;
  validationErrors?: any[];
  stepData?: any[];
  isSubmitting?: boolean;
  onFieldChange?: (fieldPath: string) => void;
}

export const ReviewStepV2: React.FC<ReviewStepV2Props> = ({ 
  data, 
  programType = 'hybrid',
  validationErrors,
  formData
}) => {
 
  // no-op

  const renderProgramSummary = () => {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm font-medium text-gray-700">Program Title</Label>
            <p className="text-sm text-gray-900">{formData?.title || 'Not set'}</p>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700">Category</Label>
            <p className="text-sm text-gray-900">
              {formData?.categoryId 
                ? (typeof formData.categoryId === 'string' 
                    ? formData.categoryId 
                    : formData.categoryId.title || formData.categoryId._id)
                : 'Not set'}
            </p>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700">Duration</Label>
            <p className="text-sm text-gray-900">
              {formData?.duration?.value ? `${formData.duration.value} ${formData.duration.isInWeeks ? 'weeks' : 'months'}` : 'Not set'}
            </p>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700">Language</Label>
            <p className="text-sm text-gray-900">{formData?.language || 'Not set'}</p>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700">Status</Label>
            <p className="text-sm text-gray-900">{formData?.status || 'Not set'}</p>
          </div>
          <div>
            <Label className="text-sm font-medium text-gray-700">Program Type</Label>
            <p className="text-sm text-gray-900">{programType || 'Not set'}</p>
          </div>
        </div>
        
        {data.subtitle && (
          <div>
            <Label className="text-sm font-medium text-gray-700">Subtitle</Label>
            <p className="text-sm text-gray-900">{data.subtitle}</p>
          </div>
        )}
        
        {data.about && (
          <div>
            <Label className="text-sm font-medium text-gray-700">About</Label>
            <div className="text-sm text-gray-900 mt-1 p-3 bg-gray-50 rounded-lg overflow-hidden break-words max-w-full">
              <div dangerouslySetInnerHTML={{ __html: data.about }} className="[&>*]:max-w-full [&>*]:break-words" />
            </div>
          </div>
        )}
        
        {data.videoUrl && (
          <div>
            <Label className="text-sm font-medium text-gray-700">Video URL</Label>
            <p className="text-sm text-gray-900 break-all">{data.videoUrl}</p>
          </div>
        )}
        
        {formData?.keyFeatures && formData.keyFeatures.length > 0 && (
          <div>
            <Label className="text-sm font-medium text-gray-700">Key Features</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {formData.keyFeatures.map((feature: any, index: number) => (
                <div key={index} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                  {feature.name}: {feature.value}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {data.nationalCertifications && data.nationalCertifications.items && data.nationalCertifications.items.length > 0 && (() => {
          const rawItems: string[] = data.nationalCertifications.items || [];
          const isAllIncluded = Boolean(data.nationalCertifications.isallIncluded);
          const includedItems = (isAllIncluded ? rawItems : rawItems.filter((s: string) => s?.trim?.().endsWith('*')))
            .map((s: string) => (typeof s === 'string' && s.endsWith('*') ? s.slice(0, -1) : s))
            .map((s: string) => (typeof s === 'string' ? s.trim() : s))
            .filter((s: any) => typeof s === 'string' && s.length > 0);

          if (includedItems.length === 0) return null;

          return (
          <div>
            <Label className="text-sm font-medium text-gray-700">National Certifications</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {includedItems.map((cert: string, index: number) => (
                <div key={index} className="px-3 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-medium">
                  {cert}
                </div>
              ))}
            </div>
          </div>
          );
        })()}
        
        {data.thisCourseIncludesData && data.thisCourseIncludesData.length > 0 && (
          <div>
            <Label className="text-sm font-medium text-gray-700">This Course Includes</Label>
            <div className="mt-1 space-y-1">
              {data.thisCourseIncludesData.map((item: any, index: number) => (
                <div key={index} className="text-sm text-gray-900 flex items-center gap-2">
                  <span className="text-green-600">✓</span> {item.name}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {formData?.whoisthisFor && formData.whoisthisFor.length > 0 && (
          <div>
            <Label className="text-sm font-medium text-gray-700">Who Is This For</Label>
            <div className="mt-1 space-y-2">
              {formData.whoisthisFor.map((item: any, index: number) => (
                <div key={index} className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">{item.title}</p>
                  <p className="text-xs text-gray-600">{item.description?.length || 0} points</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {formData?.wherecaniWork && formData.wherecaniWork.length > 0 && (
          <div>
            <Label className="text-sm font-medium text-gray-700">Where Can I Work</Label>
            <div className="mt-1 space-y-2">
              {formData.wherecaniWork.map((item: any, index: number) => (
                <div key={index} className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">{item.title}</p>
                  <p className="text-xs text-gray-600">{item.description?.length || 0} points</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {data.schedule && (
          <div>
            <Label className="text-sm font-medium text-gray-700">Schedule</Label>
            <div className="mt-1 p-2 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-900">
                {data.schedule.startMonth && data.schedule.startMonth.length > 0 
                  ? `Start Months: ${data.schedule.startMonth.join(', ')}`
                  : data.schedule.batches && data.schedule.batches.length > 0
                  ? `${data.schedule.batches.length} batch(es) configured`
                  : 'Schedule configured'}
              </p>
            </div>
          </div>
        )}
        
        {formData?.modules && formData.modules.length > 0 && (
          <div>
            <Label className="text-sm font-medium text-gray-700">Modules</Label>
            <div className="mt-1 space-y-2">
              {formData.modules.map((module: any, index: number) => (
                <div key={index} className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">{module.label || `Module ${index + 1}`}</p>
                  <p className="text-xs text-gray-600">{module.items?.length || 0} items</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {data.paymentOptions && data.paymentOptions.length > 0 && (
          <div>
            <Label className="text-sm font-medium text-gray-700">Payment Options</Label>
            <div className="mt-1 space-y-2">
              {data.paymentOptions.map((option: any, index: number) => {
                // For FULL_PAY: only need slug
                // For installment plans: need both squarePlanId and slug
                const isFullPay = option.planType === 'FULL_PAY';
                const hasSquarePlan = isFullPay 
                  ? (option.slug && option.slug.trim() !== '')
                  : (option.squarePlanId && option.squarePlanId.trim() !== '' && option.slug && option.slug.trim() !== '');
                
                return (
                  <div key={index} className={`p-2 rounded-lg border ${hasSquarePlan ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{option.displayName || `Option ${index + 1}`}</p>
                        <p className="text-xs text-gray-600">${option.youPay || 0}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        {hasSquarePlan ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="text-xs text-green-600 font-medium">Square Ready</span>
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="h-4 w-4 text-yellow-600" />
                            <span className="text-xs text-yellow-600 font-medium">
                              {isFullPay ? 'Needs Slug' : 'Needs Square Plan'}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    {hasSquarePlan && (
                      <div className="mt-1 text-xs text-green-600">
                        {isFullPay ? (
                          <>Slug: {option.slug}</>
                        ) : (
                          <>Plan ID: {option.squarePlanId} | Slug: {option.slug}</>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {data.faqs && data.faqs.length > 0 && (
          <div>
            <Label className="text-sm font-medium text-gray-700">FAQs</Label>
            <div className="mt-1 space-y-2">
              {data.faqs.map((faq: any, index: number) => (
                <div key={index} className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">{faq.question || `FAQ ${index + 1}`}</p>
                  <p className="text-xs text-gray-600 capitalize">{faq.type || 'general'}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {data.podcastData && data.podcastData.length > 0 && (
          <div>
            <Label className="text-sm font-medium text-gray-700">Podcasts</Label>
            <div className="mt-1 space-y-2">
              {data.podcastData.map((podcast: any, index: number) => (
                <div key={index} className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-900">{podcast.title || `Podcast ${index + 1}`}</p>
                  <p className="text-xs text-gray-600">{podcast.duration || 'No duration'}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <CheckCircle className="h-12 w-12 text-blue-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900">Review & Submit (V2)</h3>
        <p className="text-sm text-gray-600">Review your program configuration before submission</p>
      </div>



      {/* Program Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Program Summary</CardTitle>
        </CardHeader>
        <CardContent>
          {renderProgramSummary()}
        </CardContent>
      </Card>

      {/* Validation Errors */}
      {validationErrors && validationErrors.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="h-5 w-5" />
              Validation Errors
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {validationErrors.map((error, index) => (
                <div key={index} className="flex items-center gap-2 text-red-700">
                  <XCircle className="h-4 w-4" />
                  <span className="text-sm">{error}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}


    </div>
  );
};
